import React, { useContext, useState } from "react";
import {
  IonAlert,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonIcon,
  IonRouterLink,
} from "@ionic/react";
import {
  alertCircleOutline,
  informationCircleOutline,
  warningOutline,
} from "ionicons/icons";
import useUserAlerts from "../../hooks/users/useUserAlerts";
import { AuthenticationContext } from "../../providers/AuthenticationProvider/AuthenticationProvider";
import "./AlertsList.css";

// One place that renders every "specchietto" (certificate / membership /
// subscription expiry, unpaid packages) coming from the single `/user-alerts`
// source. Persistent and tappable, replacing the old scattered toasts/cards.
const COLOR = { danger: "danger", warning: "warning", info: "medium" };
const ICON = {
  danger: alertCircleOutline,
  warning: warningOutline,
  info: informationCircleOutline,
};

// "2 lug 2026" — compact date for one package's validity range.
const formatDate = (ms) =>
  ms == null
    ? null
    : new Date(ms).toLocaleDateString("it-IT", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });

// One line per unpaid package: "• Pacchetto Coach (dal 2 lug al 28 lug 2026)".
const detailLine = (detail) => {
  const from = formatDate(detail.from);
  const to = formatDate(detail.to);
  const range = from && to ? ` (dal ${from} al ${to})` : "";
  return `• ${detail.description}${range}`;
};

function AlertsList() {
  const { appUserId } = useContext(AuthenticationContext);
  const { data: alerts, isLoading } = useUserAlerts(appUserId);
  const [detailsAlert, setDetailsAlert] = useState(null);

  if (isLoading || !alerts || alerts.length === 0) {
    return null;
  }

  return (
    <div className="AlertsList">
      {alerts.map((alert) => {
        const hasDetails = alert.details?.length > 0;
        const card = (
          <IonCard
            color={COLOR[alert.severity] || "warning"}
            // Without a route the card itself opens the details popup (same
            // action as the ⓘ, with a bigger tap target).
            onClick={
              !alert.route && hasDetails
                ? () => setDetailsAlert(alert)
                : undefined
            }
          >
            <IonCardHeader>
              <IonCardTitle
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: ".5rem",
                  fontSize: "1rem",
                }}
              >
                <IonIcon icon={ICON[alert.severity] || warningOutline} />
                {alert.message}
                {hasDetails && (
                  <IonIcon
                    icon={informationCircleOutline}
                    role="button"
                    aria-label={`Dettagli: ${alert.title}`}
                    style={{
                      marginLeft: "auto",
                      fontSize: "1.4rem",
                      flexShrink: 0,
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      // Keep the popup a single action: never bubble up into
                      // the card's own click or a wrapping router link.
                      e.preventDefault();
                      e.stopPropagation();
                      setDetailsAlert(alert);
                    }}
                  />
                )}
              </IonCardTitle>
            </IonCardHeader>
          </IonCard>
        );

        // Only alerts with a route (pages the user can actually open, e.g.
        // /profile) are wrapped in a link.
        return alert.route ? (
          <IonRouterLink
            key={alert.category}
            routerLink={alert.route}
            style={{ textDecoration: "none" }}
          >
            {card}
          </IonRouterLink>
        ) : (
          <React.Fragment key={alert.category}>{card}</React.Fragment>
        );
      })}
      <IonAlert
        isOpen={!!detailsAlert}
        header={detailsAlert?.title}
        cssClass="alerts-list-details"
        message={(detailsAlert?.details || []).map(detailLine).join("\n")}
        buttons={["OK"]}
        onDidDismiss={() => setDetailsAlert(null)}
      />
    </div>
  );
}

export default AlertsList;
